import { Drawer, useFormSubmissionController } from '@melio/penny';
import { Collaborator, useCollaborator } from '@melio/platform-api';
import { useMelioIntl } from '@melio/platform-i18n';
import { noop } from 'lodash';
import { useState } from 'react';

import { useCollaboratorsPermissions } from '../../settings/Collaborators/useCollaboratorsPermissions';
import { CollaboratorDrawerBody } from '../components/CollaboratorDrawerBody';
import { CollaboratorDrawerFooter } from '../components/CollaboratorDrawerFooter';
import { CollaboratorDrawerHeader } from '../components/CollaboratorDrawerHeader';
import { FormFields } from '../types';
import { useSchema } from '../useSchema';

export type EditCollaboratorDrawerActivityProps = { collaboratorId: Collaborator['id']; onClose: VoidFunction };
export const EditCollaboratorDrawerActivity = ({ collaboratorId, onClose }: EditCollaboratorDrawerActivityProps) => {
  const { formatMessage } = useMelioIntl();
  const [isOpen, setIsOpen] = useState(true);
  const { onSubmissionStateChange, submitButtonProps, cancelButtonProps } = useFormSubmissionController<FormFields>();
  const schema = useSchema();
  const { canEditCollaborator } = useCollaboratorsPermissions();

  const { data: actor, isLoading: isLoadingActor } = useCollaborator<'user'>({ id: 'me' });
  const { data: collaborator, isLoading: isLoadingCollaborator } = useCollaborator<'user'>({ id: collaboratorId });

  const isLoading = isLoadingActor || isLoadingCollaborator;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isViewMode = !canEditCollaborator || actor?.id === collaborator?.id; // TODO: use this when we have other fields

  return (
    <Drawer
      data-testid="edit-collaborator-drawer"
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      onCloseComplete={onClose}
      isLoading={isLoading}
      header={collaborator ? <CollaboratorDrawerHeader collaborator={collaborator} /> : null}
      body={
        collaborator ? (
          <CollaboratorDrawerBody
            isLoading={isLoading}
            isSaving={false}
            onSubmissionStateChange={onSubmissionStateChange}
            onSubmit={noop}
            schema={schema}
            formFieldsSettings={{
              firstName: { readonly: true, required: true },
              lastName: { readonly: true, required: true },
              email: { readonly: true, required: true },
            }}
            defaultValues={{
              firstName: collaborator.user.firstName,
              lastName: collaborator.user.lastName,
              email: collaborator.user.email ?? undefined,
            }}
          />
        ) : null
      }
      footer={
        collaborator ? (
          <CollaboratorDrawerFooter
            submitLabel={formatMessage('activities.collaboratorDrawer.edit.footer.cta')}
            submitButtonProps={submitButtonProps}
            cancelLabel={formatMessage('activities.collaboratorDrawer.edit.footer.tertiary')}
            cancelButtonProps={cancelButtonProps}
          />
        ) : null
      }
    />
  );
};
